import { z } from 'zod';
import { asRecord, num, text } from './resultValues.js';

const nonBlank = (field: string) =>
  z.string().refine((value) => value.trim().length > 0, `${field} must not be blank`);

export const risingElementsSchema = z.object({
  elements: z.array(
    z.looseObject({
      feature: nonBlank('Feature'),
      tag: nonBlank('Tag'),
    }),
  ),
});

export type RisingElementList = z.infer<typeof risingElementsSchema>;

// The service's own row key; a pick is read back by it.
const KEY_FIELDS = ['feature', 'tag', 'network'] as const;

export interface RisingElement {
  key: string;
  feature: string;
  tag: string;
  network: string | null;
  measure: string | null;
  measureLabel: string | null;
  previousValue: number | null;
  currentValue: number | null;
  changePercent: number | null;
  creativeCount: number;
}

export interface RisingGroup {
  category: string;
  elements: RisingElement[];
}

export function isRisingResult(result: unknown): boolean {
  return asRecord(result)?.['result_type'] === 'rising_elements';
}

export function risingKeyOf(record: RisingElementList['elements'][number]): string {
  return KEY_FIELDS.map((field) => text(record[field]) ?? '').join('|');
}

function risingOf(record: RisingElementList['elements'][number]): RisingElement {
  return {
    key: risingKeyOf(record),
    feature: record.feature,
    tag: record.tag,
    network: text(record['network']),
    measure: text(record['measure']),
    measureLabel: text(record['measure_label']),
    previousValue: num(record['previous_value']),
    currentValue: num(record['current_value']),
    changePercent: num(record['change_percent']),
    creativeCount: num(record['creative_count']) ?? 0,
  };
}

export function risingGroupsOf(result: RisingElementList): RisingGroup[] {
  const groups = new Map<string, RisingGroup>();
  for (const record of result.elements) {
    const category = text(record['category']) ?? 'Uncategorised';
    const group = groups.get(category) ?? { category, elements: [] };
    group.elements.push(risingOf(record));
    groups.set(category, group);
  }
  return [...groups.values()];
}
